import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  CreditCard, 
  Gamepad2, 
  LogOut,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Users,
  Power,
  RefreshCw,
  AlertCircle,
  Trophy,
  Plus,
  Edit,
  Trash2,
  MessageSquare,
  Mail,
  Smartphone,
  BarChart3,
  MapPin,
  Upload,
  Package,
  Award,
  Phone
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = window.location.origin;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [adminUsername, setAdminUsername] = useState('');
  
  // Payment states
  const [pendingPayments, setPendingPayments] = useState([]);
  const [paymentStats, setPaymentStats] = useState({});
  const [processingPaymentId, setProcessingPaymentId] = useState(null);
  
  // Game states
  const [games, setGames] = useState([]);
  const [togglingGameId, setTogglingGameId] = useState(null);
  const [competitionInputs, setCompetitionInputs] = useState({});
  
  // Prize states
  const [prizes, setPrizes] = useState([]);
  const [editingPrize, setEditingPrize] = useState(null);
  const [newPrize, setNewPrize] = useState({ position: 1, prize_name: '', prize_description: '' });
  
  // Leaderboard states
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardFilters, setLeaderboardFilters] = useState({ game_id: '', competition_date: '' });
  
  // Contact Messages states
  const [contactMessages, setContactMessages] = useState([]);
  const [contactFilter, setContactFilter] = useState('all');
  
  // PWA Installs states
  const [pwaStats, setPwaStats] = useState(null);
  
  // Winners states
  const [pendingWinners, setPendingWinners] = useState([]);
  const [allWinners, setAllWinners] = useState([]);
  const [selectedWinner, setSelectedWinner] = useState(null);
  
  // Users states
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [pwaLoading, setPwaLoading] = useState(false);
  const [pwaDays, setPwaDays] = useState(30);

  useEffect(() => {
    verifyAdminSession();
  }, []);

  useEffect(() => {
    if (!loading) {
      if (activeTab === 'payments') {
        fetchPayments();
      } else if (activeTab === 'games') {
        fetchGames();
      } else if (activeTab === 'prizes') {
        fetchPrizes();
      } else if (activeTab === 'leaderboard') {
        fetchGames(); // Fetch games for filter dropdown
        fetchLeaderboard();
      } else if (activeTab === 'contact') {
        fetchContactMessages();
      } else if (activeTab === 'pwa') {
        fetchPWAStats();
      } else if (activeTab === 'users') {
        fetchUsers();
      } else if (activeTab === 'dashboard') {
        fetchPaymentStats();
      } else if (activeTab === 'winners') {
        fetchWinners();
      }
    }
  }, [activeTab, loading]);

  const verifyAdminSession = async () => {
    const sessionToken = localStorage.getItem('admin_session_token');
    
    if (!sessionToken) {
      navigate('/admin/login');
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/verify-session?session_token=${sessionToken}`);
      
      if (response.data.valid) {
        setAdminUsername(response.data.username);
        setLoading(false);
      } else {
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Session verification failed:', error);
      navigate('/admin/login');
    }
  };

  const handleLogout = async () => {
    const sessionToken = localStorage.getItem('admin_session_token');
    
    try {
      await axios.post(`${BACKEND_URL}/api/admin/logout?session_token=${sessionToken}`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('admin_session_token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  // Payment functions
  const fetchPayments = async () => {
    try {
      const [paymentsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/payments/pending`),
        axios.get(`${BACKEND_URL}/api/admin/payments/stats`)
      ]);
      
      setPendingPayments(paymentsRes.data.pending_payments);
      setPaymentStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    }
  };

  const fetchPaymentStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/payments/stats`);
      setPaymentStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleVerifyPayment = async (paymentId, action) => {
    setProcessingPaymentId(paymentId);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/payments/verify`, {
        payment_id: paymentId,
        action: action,
        admin_id: adminUsername
      });

      if (response.data.success) {
        toast.success(`Payment ${action}d successfully!`);
        fetchPayments();
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      toast.error('Failed to verify payment');
    } finally {
      setProcessingPaymentId(null);
    }
  };

  // Game functions
  const fetchGames = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/games/config`);
      setGames(response.data.games);
    } catch (error) {
      console.error('Error fetching games:', error);
      toast.error('Failed to load games');
    }
  };

  const activateSingleGame = async (gameId) => {
    setTogglingGameId(gameId);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/games/activate-one?game_id=${gameId}`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchGames();
      }
    } catch (error) {
      console.error('Error activating game:', error);
      toast.error('Failed to activate game');
    } finally {
      setTogglingGameId(null);
    }
  };

  const toggleCompetitionMode = async (gameId, enable, entryFee, durationHours, scheduleDate, scheduleTime, timezone) => {
    try {
      const payload = {
        game_id: gameId,
        competition_mode: enable
      };

      if (enable) {
        if (!entryFee || entryFee < 1) {
          toast.error('Please enter a valid entry fee (minimum ₹1)');
          return;
        }
        if (!durationHours || durationHours < 1) {
          toast.error('Please enter a valid duration (minimum 1 hour)');
          return;
        }
        payload.entry_fee = parseInt(entryFee);
        payload.duration_hours = parseFloat(durationHours);
        
        // Handle scheduled competition ONLY if both date AND time are filled
        if (scheduleDate && scheduleTime && scheduleDate.trim() !== '' && scheduleTime.trim() !== '') {
          const scheduledDateTime = `${scheduleDate}T${scheduleTime}:00`;
          payload.scheduled_start = scheduledDateTime;
          payload.timezone = timezone || 'Asia/Kolkata';
          toast.info('Competition scheduled! It will start automatically at the specified time.');
        } else {
          // Immediate start - do NOT send scheduled_start at all
          toast.success('Starting competition immediately!');
        }
      }

      const response = await axios.post(`${BACKEND_URL}/api/admin/games/competition/toggle`, payload);

      if (response.data.success) {
        toast.success(response.data.message);
        fetchGames();
      }
    } catch (error) {
      console.error('Error toggling competition mode:', error);
      toast.error(error.response?.data?.message || 'Failed to toggle competition mode');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata'
    });
  };

  const formatDateIST = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Asia/Kolkata',
      hour12: true
    });
  };

  const getGameIcon = (gameId) => {
    const icons = {
      'tap-challenge': '👆',
      'memory-puzzle': '🧩',
      'lightning-quiz': '⚡',
      'reaction-test': '⏱️',
      'flappy-tap': '🐦',
      'ball-balance': '⚖️',
      'target-hit': '🎯',
      'timing-tap': '⏰',
      'ball-jump': '📈',
      'bottle-flip': '🍾',
      'tap-flash': '⚡'
    };
    return icons[gameId] || '🎮';
  };

  // Prize Management Functions
  const fetchPrizes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/prizes`);
      setPrizes(response.data.prizes);
    } catch (error) {
      console.error('Error fetching prizes:', error);
      toast.error('Failed to load prizes');
    }
  };

  const handleCreatePrize = async () => {
    if (!newPrize.prize_name.trim()) {
      toast.error('Please enter a prize name');
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/admin/prizes`, newPrize);
      toast.success('Prize added successfully');
      setNewPrize({ position: prizes.length + 1, prize_name: '', prize_description: '' });
      fetchPrizes();
    } catch (error) {
      console.error('Error creating prize:', error);
      toast.error('Failed to add prize');
    }
  };

  const handleUpdatePrize = async (prizeId, updates) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/prizes/${prizeId}`, updates);
      toast.success('Prize updated successfully');
      setEditingPrize(null);
      fetchPrizes();
    } catch (error) {
      console.error('Error updating prize:', error);
      toast.error('Failed to update prize');
    }
  };

  const handleDeletePrize = async (prizeId) => {
    if (!window.confirm('Are you sure you want to delete this prize?')) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/prizes/${prizeId}`);
      toast.success('Prize deleted successfully');
      fetchPrizes();
    } catch (error) {
      console.error('Error deleting prize:', error);
      toast.error('Failed to delete prize');
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const params = new URLSearchParams();
      if (leaderboardFilters.game_id) params.append('game_id', leaderboardFilters.game_id);
      if (leaderboardFilters.competition_date) params.append('competition_date', leaderboardFilters.competition_date);
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/leaderboard?${params.toString()}`);
      setLeaderboard(response.data.leaderboard);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      toast.error('Failed to load leaderboard');
    }
  };

  const fetchContactMessages = async () => {
    try {
      const params = new URLSearchParams();
      if (contactFilter !== 'all') {
        params.append('status', contactFilter);
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/contact-messages?${params.toString()}`);
      setContactMessages(response.data.messages);
    } catch (error) {
      console.error('Error fetching contact messages:', error);
      toast.error('Failed to load contact messages');
    }
  };

  const handleUpdateContactStatus = async (messageId, status) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/contact-messages/${messageId}`, { status });
      toast.success('Status updated successfully');
      fetchContactMessages();
    } catch (error) {
      console.error('Error updating contact status:', error);
      toast.error('Failed to update status');
    }
  };

  const fetchWinners = async () => {
    try {
      const [pendingRes, allRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/winners/pending`),
        axios.get(`${BACKEND_URL}/api/admin/winners/all`)
      ]);
      
      setPendingWinners(pendingRes.data.winners || []);
      setAllWinners(allRes.data.winners || []);
    } catch (error) {
      console.error('Error fetching winners:', error);
      toast.error('Failed to load winners');
    }
  };

  const fetchPWAStats = async () => {
    setPwaLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${BACKEND_URL}/api/admin/pwa/installs?days=${pwaDays}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPwaStats(response.data);
    } catch (error) {
      console.error('Error fetching PWA stats:', error);
      toast.error('Failed to load PWA statistics');
    } finally {
      setPwaLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${BACKEND_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-yellow-400 mx-auto mb-4"></div>
          <p className="text-white text-xl">Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  const activeGame = games.find(g => g.is_active);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black pt-24 lg:pt-28">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-gray-400 text-sm">Welcome, {adminUsername}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-gray-900/50 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-2 overflow-x-auto">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition-colors ${
                activeTab === 'dashboard'
                  ? 'text-yellow-400 border-b-2 border-yellow-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <LayoutDashboard className="w-5 h-5" />
              Overview
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition-colors ${
                activeTab === 'payments'
                  ? 'text-yellow-400 border-b-2 border-yellow-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <CreditCard className="w-5 h-5" />
              Payments
              {pendingPayments.length > 0 && (
                <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingPayments.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('games')}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition-colors ${
                activeTab === 'games'
                  ? 'text-yellow-400 border-b-2 border-yellow-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Gamepad2 className="w-5 h-5" />
              Games
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition-colors ${
                activeTab === 'users'
                  ? 'text-yellow-400 border-b-2 border-yellow-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Users className="w-5 h-5" />
              Users
              {users.length > 0 && (
                <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                  {users.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('prizes')}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition-colors ${
                activeTab === 'prizes'
                  ? 'text-yellow-400 border-b-2 border-yellow-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Trophy className="w-5 h-5" />
              Prizes
            </button>
            <button
              onClick={() => setActiveTab('leaderboard')}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition-colors ${
                activeTab === 'leaderboard'
                  ? 'text-yellow-400 border-b-2 border-yellow-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Users className="w-5 h-5" />
              Leaderboard
            </button>
            <button
              onClick={() => setActiveTab('contact')}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition-colors ${
                activeTab === 'contact'
                  ? 'text-yellow-400 border-b-2 border-yellow-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <MessageSquare className="w-5 h-5" />
              Contact Messages
              {contactMessages.filter(m => m.status === 'pending').length > 0 && (
                <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">
                  {contactMessages.filter(m => m.status === 'pending').length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('pwa')}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition-colors ${
                activeTab === 'pwa'
                  ? 'text-yellow-400 border-b-2 border-yellow-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Smartphone className="w-5 h-5" />
              PWA Installs
            </button>
            <button
              onClick={() => setActiveTab('winners')}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition-colors ${
                activeTab === 'winners'
                  ? 'text-yellow-400 border-b-2 border-yellow-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Trophy className="w-5 h-5" />
              Winners
              {pendingWinners.length > 0 && (
                <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingWinners.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 rounded-lg p-6 border border-blue-700/50">
                <div className="flex items-center justify-between mb-2">
                  <Clock className="w-8 h-8 text-blue-400" />
                  <span className="text-3xl font-bold text-white">{paymentStats.pending_payments || 0}</span>
                </div>
                <p className="text-blue-300 text-sm">Pending Payments</p>
              </div>

              <div className="bg-gradient-to-br from-green-900/50 to-green-800/30 rounded-lg p-6 border border-green-700/50">
                <div className="flex items-center justify-between mb-2">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                  <span className="text-3xl font-bold text-white">{paymentStats.verified_payments || 0}</span>
                </div>
                <p className="text-green-300 text-sm">Verified</p>
              </div>

              <div className="bg-gradient-to-br from-red-900/50 to-red-800/30 rounded-lg p-6 border border-red-700/50">
                <div className="flex items-center justify-between mb-2">
                  <XCircle className="w-8 h-8 text-red-400" />
                  <span className="text-3xl font-bold text-white">{paymentStats.rejected_payments || 0}</span>
                </div>
                <p className="text-red-300 text-sm">Rejected</p>
              </div>

              <div className="bg-gradient-to-br from-yellow-900/50 to-yellow-800/30 rounded-lg p-6 border border-yellow-700/50">
                <div className="flex items-center justify-between mb-2">
                  <DollarSign className="w-8 h-8 text-yellow-400" />
                  <span className="text-3xl font-bold text-white">₹{paymentStats.total_verified_amount || 0}</span>
                </div>
                <p className="text-yellow-300 text-sm">Total Revenue</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-900/50 to-blue-900/50 rounded-lg p-6 border border-purple-700/50">
              <h2 className="text-xl font-bold text-white mb-4">Active Game</h2>
              {activeGame ? (
                <div className="flex items-center gap-4">
                  <span className="text-6xl">{getGameIcon(activeGame.game_id)}</span>
                  <div>
                    <h3 className="text-2xl font-bold text-white">{activeGame.game_name}</h3>
                    <p className="text-purple-300">Currently live for all players</p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-400">No game is currently active</p>
              )}
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-white">Payment Verification</h2>
              <button
                onClick={fetchPayments}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>

            <div className="bg-gray-900/50 rounded-lg border border-gray-800 overflow-hidden">
              {pendingPayments.length === 0 ? (
                <div className="p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">No pending payments!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-800/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Game</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Transaction ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Submitted</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {pendingPayments.map((payment) => (
                        <tr key={payment.payment_id} className="hover:bg-gray-800/30">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-white">{payment.user_name}</div>
                            <div className="text-sm text-gray-400">{payment.user_email}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-white">{payment.game_name}</td>
                          <td className="px-6 py-4 text-sm font-semibold text-yellow-400">₹{payment.amount}</td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-300 font-mono bg-gray-800 px-2 py-1 rounded">
                              {payment.upi_transaction_id}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-400">{formatDate(payment.submitted_at)}</td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleVerifyPayment(payment.payment_id, 'approve')}
                                disabled={processingPaymentId === payment.payment_id}
                                className="flex items-center gap-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-3 py-1.5 rounded-lg text-sm transition-colors"
                              >
                                <CheckCircle className="w-4 h-4" />
                                Approve
                              </button>
                              <button
                                onClick={() => handleVerifyPayment(payment.payment_id, 'reject')}
                                disabled={processingPaymentId === payment.payment_id}
                                className="flex items-center gap-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white px-3 py-1.5 rounded-lg text-sm transition-colors"
                              >
                                <XCircle className="w-4 h-4" />
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Games Tab */}
        {activeTab === 'games' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-white">Game Control</h2>
              <button
                onClick={fetchGames}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>

            <div className="bg-gray-900/50 rounded-lg border border-gray-800 divide-y divide-gray-800">
              {games.map((game) => {
                const isCompetitionActive = game.is_competition_mode;
                const hasCompetitionEnded = game.competition_end_time && new Date(game.competition_end_time) < new Date();
                
                return (
                  <div
                    key={game.game_id}
                    className={`p-6 hover:bg-gray-800/30 transition-colors ${
                      game.is_active ? 'bg-green-900/20' : ''
                    } ${isCompetitionActive ? 'border-l-4 border-yellow-500' : ''}`}
                  >
                    {/* Game Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className="text-5xl">{getGameIcon(game.game_id)}</div>
                        <div>
                          <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            {game.game_name}
                            {game.is_active && (
                              <span className="px-2 py-1 bg-green-600 text-white text-xs rounded-full">PRACTICE</span>
                            )}
                            {isCompetitionActive && !hasCompetitionEnded && (
                              <span className="px-2 py-1 bg-yellow-600 text-black text-xs rounded-full font-bold">💰 COMPETITION</span>
                            )}
                          </h3>
                          <p className="text-sm text-gray-400 capitalize">{game.game_id}</p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        {game.is_active ? (
                          <div className="flex items-center gap-2 text-green-400 font-semibold">
                            <Power className="w-5 h-5" />
                            <span>Active</span>
                          </div>
                        ) : (
                          <button
                            onClick={() => activateSingleGame(game.game_id)}
                            disabled={togglingGameId === game.game_id}
                            className="flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 text-black font-bold px-6 py-3 rounded-lg transition-all"
                          >
                            {togglingGameId === game.game_id ? (
                              <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                                <span>Activating...</span>
                              </>
                            ) : (
                              <>
                                <Power className="w-5 h-5" />
                                <span>Activate</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Competition Mode Section */}
                    <div className="mt-4 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-lg font-semibold text-yellow-400 flex items-center gap-2">
                          <Trophy className="w-5 h-5" />
                          Competition Mode
                        </h4>
                        {isCompetitionActive && (
                          <span className={`text-xs px-2 py-1 rounded-full ${hasCompetitionEnded ? 'bg-red-600' : 'bg-green-600'} text-white`}>
                            {hasCompetitionEnded ? 'Ended' : 'Live'}
                          </span>
                        )}
                      </div>

                      {isCompetitionActive && !hasCompetitionEnded ? (
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-400">Entry Fee:</span>
                              <span className="text-white font-semibold ml-2">₹{game.competition_entry_fee}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Prize Pool:</span>
                              <span className="text-green-400 font-semibold ml-2">₹{game.competition_entry_fee * 10}</span>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-400">Start:</span>
                              <span className="text-white ml-2">{formatDate(game.competition_start_time)}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">End:</span>
                              <span className="text-white ml-2">{formatDate(game.competition_end_time)}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => toggleCompetitionMode(game.game_id, false)}
                            className="w-full mt-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors font-semibold"
                          >
                            End Competition Now
                          </button>
                        </div>
                      ) : isCompetitionActive && hasCompetitionEnded ? (
                        <div className="space-y-2">
                          <div className="bg-red-900/30 border border-red-700 rounded p-3 text-sm">
                            <p className="text-red-300 mb-2">Competition has ended. Reset to start a new competition.</p>
                            <div className="grid grid-cols-2 gap-4 text-xs text-gray-400">
                              <div>
                                <span>Previous Entry Fee:</span>
                                <span className="text-white ml-2">₹{game.competition_entry_fee}</span>
                              </div>
                              <div>
                                <span>Ended:</span>
                                <span className="text-white ml-2">{formatDate(game.competition_end_time)}</span>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => toggleCompetitionMode(game.game_id, false)}
                            className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors font-semibold"
                          >
                            Reset Competition Mode
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-gray-400 text-sm mb-1">Entry Fee (₹)</label>
                              <input
                                type="number"
                                min="1"
                                placeholder="e.g. 10"
                                value={competitionInputs[game.game_id]?.entryFee || ''}
                                onChange={(e) => setCompetitionInputs({
                                  ...competitionInputs,
                                  [game.game_id]: {
                                    ...competitionInputs[game.game_id],
                                    entryFee: e.target.value
                                  }
                                })}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-gray-400 text-sm mb-1">Duration (hours)</label>
                              <input
                                type="number"
                                min="1"
                                placeholder="e.g. 24"
                                value={competitionInputs[game.game_id]?.duration || ''}
                                onChange={(e) => setCompetitionInputs({
                                  ...competitionInputs,
                                  [game.game_id]: {
                                    ...competitionInputs[game.game_id],
                                    duration: e.target.value
                                  }
                                })}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                              />
                            </div>
                          </div>
                          
                          {/* Schedule Competition Section */}
                          <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-3 space-y-3">
                            <div className="flex items-center gap-2 text-blue-400 text-sm font-semibold">
                              <Clock className="w-4 h-4" />
                              <span>Schedule Competition (Optional)</span>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-gray-400 text-xs mb-1">Start Date</label>
                                <input
                                  type="date"
                                  value={competitionInputs[game.game_id]?.scheduleDate || ''}
                                  onChange={(e) => setCompetitionInputs({
                                    ...competitionInputs,
                                    [game.game_id]: {
                                      ...competitionInputs[game.game_id],
                                      scheduleDate: e.target.value
                                    }
                                  })}
                                  className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-gray-400 text-xs mb-1">Start Time</label>
                                <input
                                  type="time"
                                  value={competitionInputs[game.game_id]?.scheduleTime || ''}
                                  onChange={(e) => setCompetitionInputs({
                                    ...competitionInputs,
                                    [game.game_id]: {
                                      ...competitionInputs[game.game_id],
                                      scheduleTime: e.target.value
                                    }
                                  })}
                                  className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-sm"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-gray-400 text-xs mb-1">Timezone</label>
                              <select
                                value={competitionInputs[game.game_id]?.timezone || 'Asia/Kolkata'}
                                onChange={(e) => setCompetitionInputs({
                                  ...competitionInputs,
                                  [game.game_id]: {
                                    ...competitionInputs[game.game_id],
                                    timezone: e.target.value
                                  }
                                })}
                                className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-sm"
                              >
                                <option value="Asia/Kolkata">India (IST)</option>
                                <option value="America/New_York">New York (EST)</option>
                                <option value="Europe/London">London (GMT)</option>
                                <option value="Asia/Dubai">Dubai (GST)</option>
                                <option value="Asia/Singapore">Singapore (SGT)</option>
                              </select>
                            </div>
                            <p className="text-xs text-gray-500">
                              Leave empty to start immediately
                            </p>
                          </div>
                          
                          <button
                            onClick={() => toggleCompetitionMode(
                              game.game_id, 
                              true, 
                              competitionInputs[game.game_id]?.entryFee,
                              competitionInputs[game.game_id]?.duration,
                              competitionInputs[game.game_id]?.scheduleDate,
                              competitionInputs[game.game_id]?.scheduleTime,
                              competitionInputs[game.game_id]?.timezone
                            )}
                            className="w-full bg-yellow-600 hover:bg-yellow-700 text-black font-bold px-4 py-2 rounded-lg transition-colors"
                          >
                            {competitionInputs[game.game_id]?.scheduleDate && competitionInputs[game.game_id]?.scheduleTime 
                              ? '📅 Schedule Competition' 
                              : '🚀 Start Now'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-900/20 border border-blue-800/50 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-blue-400 font-semibold mb-2">Practice Mode:</h3>
                    <ul className="text-gray-300 text-sm space-y-1">
                      <li>• All games are always available in FREE practice mode</li>
                      <li>• Players can play unlimited times to build skills</li>
                      <li>• Activate/deactivate games for practice mode visibility</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-900/20 border border-yellow-800/50 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <Trophy className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-yellow-400 font-semibold mb-2">Competition Mode:</h3>
                    <ul className="text-gray-300 text-sm space-y-1">
                      <li>• Start paid competitions for any game</li>
                      <li>• Set entry fee and competition duration</li>
                      <li>• Multiple competitions can run simultaneously</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-400" />
                Registered Users ({users.length})
              </h2>
            </div>

            {usersLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-400 mx-auto"></div>
                <p className="text-gray-400 mt-4">Loading users...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-800">
                      <th className="px-4 py-3 text-gray-300 font-semibold">#</th>
                      <th className="px-4 py-3 text-gray-300 font-semibold">Name</th>
                      <th className="px-4 py-3 text-gray-300 font-semibold">Email</th>
                      <th className="px-4 py-3 text-gray-300 font-semibold">Phone</th>
                      <th className="px-4 py-3 text-gray-300 font-semibold">City</th>
                      <th className="px-4 py-3 text-gray-300 font-semibold">Games Played</th>
                      <th className="px-4 py-3 text-gray-300 font-semibold">Highest Score</th>
                      <th className="px-4 py-3 text-gray-300 font-semibold">Competitions</th>
                      <th className="px-4 py-3 text-gray-300 font-semibold">Joined</th>
                      <th className="px-4 py-3 text-gray-300 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => (
                      <tr key={user.id} className="border-b border-gray-700 hover:bg-gray-800">
                        <td className="px-4 py-3 text-gray-400">{index + 1}</td>
                        <td className="px-4 py-3 text-white font-semibold">{user.name}</td>
                        <td className="px-4 py-3 text-blue-400">{user.email}</td>
                        <td className="px-4 py-3 text-gray-300">{user.phone}</td>
                        <td className="px-4 py-3 text-gray-300">{user.city}</td>
                        <td className="px-4 py-3 text-center text-yellow-400">{user.games_played}</td>
                        <td className="px-4 py-3 text-center text-green-400">{user.highest_score}</td>
                        <td className="px-4 py-3 text-center text-purple-400">{user.competitions_participated}</td>
                        <td className="px-4 py-3 text-gray-400 text-sm">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs ${user.is_active ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {users.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    No users registered yet
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Prizes Tab */}
        {activeTab === 'prizes' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <Trophy className="w-8 h-8 text-yellow-400" />
                Prize Management
              </h2>
            </div>

            {/* Add New Prize */}
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-lg p-6 border border-gray-800">
              <h3 className="text-xl font-semibold text-white mb-4">Add New Prize</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Position (1st, 2nd, 3rd...)</label>
                  <input
                    type="number"
                    min="1"
                    value={newPrize.position}
                    onChange={(e) => setNewPrize({ ...newPrize, position: parseInt(e.target.value) })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-yellow-400 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Prize Name *</label>
                  <input
                    type="text"
                    value={newPrize.prize_name}
                    onChange={(e) => setNewPrize({ ...newPrize, prize_name: e.target.value })}
                    placeholder="e.g., Smart Watch"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-yellow-400 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Description (Optional)</label>
                  <input
                    type="text"
                    value={newPrize.prize_description}
                    onChange={(e) => setNewPrize({ ...newPrize, prize_description: e.target.value })}
                    placeholder="e.g., Premium smartwatch"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-yellow-400 outline-none"
                  />
                </div>
              </div>
              <button
                onClick={handleCreatePrize}
                className="mt-4 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold px-6 py-2 rounded-lg transition-colors flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add Prize
              </button>
            </div>

            {/* Current Prizes */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {prizes.map((prize) => (
                <div
                  key={prize._id}
                  className="bg-gradient-to-br from-gray-900 to-black rounded-lg p-6 border border-gray-800 hover:border-yellow-400/50 transition-colors"
                >
                  {editingPrize === prize._id ? (
                    <div className="space-y-3">
                      <input
                        type="text"
                        defaultValue={prize.prize_name}
                        onBlur={(e) => {
                          if (e.target.value !== prize.prize_name) {
                            handleUpdatePrize(prize._id, { prize_name: e.target.value });
                          }
                        }}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-yellow-400 outline-none"
                      />
                      <input
                        type="text"
                        defaultValue={prize.prize_description || ''}
                        onBlur={(e) => {
                          if (e.target.value !== prize.prize_description) {
                            handleUpdatePrize(prize._id, { prize_description: e.target.value });
                          }
                        }}
                        placeholder="Description"
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-yellow-400 outline-none"
                      />
                      <button
                        onClick={() => setEditingPrize(null)}
                        className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
                      >
                        Done
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="bg-yellow-400/20 text-yellow-400 rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg">
                            {prize.position}
                          </div>
                          <div>
                            <h3 className="text-white font-semibold text-lg">{prize.prize_name}</h3>
                            {prize.prize_description && (
                              <p className="text-gray-400 text-sm">{prize.prize_description}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <button
                          onClick={() => setEditingPrize(prize._id)}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeletePrize(prize._id)}
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {prizes.length === 0 && (
              <div className="bg-gray-900/50 rounded-lg p-12 text-center border border-gray-800">
                <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-white text-xl font-semibold mb-2">No Prizes Yet</h3>
                <p className="text-gray-400">Add your first prize to get started!</p>
              </div>
            )}

            <div className="bg-blue-900/20 border border-blue-800/50 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-blue-400 font-semibold mb-2">How Prize Management Works:</h3>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• Set up prizes for different positions (1st, 2nd, 3rd, etc.)</li>
                    <li>• You can have as few or as many prizes as you want</li>
                    <li>• Edit or delete prizes anytime</li>
                    <li>• Only active prizes will be displayed to users</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="space-y-6">
            <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-800">
              <h2 className="text-2xl font-bold text-yellow-400 mb-6 flex items-center gap-2">
                <Users className="w-6 h-6" />
                Player Leaderboard
              </h2>

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Filter by Game</label>
                  <select
                    value={leaderboardFilters.game_id}
                    onChange={(e) => {
                      setLeaderboardFilters({ ...leaderboardFilters, game_id: e.target.value });
                    }}
                    className="w-full bg-black/30 text-white border border-gray-700 rounded-lg px-4 py-2"
                  >
                    <option value="">All Games</option>
                    {games.map((game) => (
                      <option key={game.game_id} value={game.game_id}>
                        {game.game_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Competition Date</label>
                  <input
                    type="date"
                    value={leaderboardFilters.competition_date}
                    onChange={(e) => {
                      setLeaderboardFilters({ ...leaderboardFilters, competition_date: e.target.value });
                    }}
                    className="w-full bg-black/30 text-white border border-gray-700 rounded-lg px-4 py-2"
                  />
                </div>

                <div className="flex items-end">
                  <button
                    onClick={fetchLeaderboard}
                    className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold px-4 py-2 rounded-lg transition-colors"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>

              {/* Leaderboard Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-gray-400 font-semibold">Rank</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-semibold">Player</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-semibold">City</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-semibold">Game</th>
                      <th className="text-center py-3 px-4 text-gray-400 font-semibold">Score</th>
                      <th className="text-center py-3 px-4 text-gray-400 font-semibold">Points</th>
                      <th className="text-center py-3 px-4 text-gray-400 font-semibold">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.length > 0 ? (
                      leaderboard.map((entry, idx) => (
                        <tr
                          key={idx}
                          className={`border-b border-gray-800 hover:bg-white/5 ${
                            entry.rank <= 3 ? 'bg-yellow-500/10' : ''
                          }`}
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {entry.rank === 1 && <span className="text-2xl">🥇</span>}
                              {entry.rank === 2 && <span className="text-2xl">🥈</span>}
                              {entry.rank === 3 && <span className="text-2xl">🥉</span>}
                              <span className="text-white font-bold">{entry.rank}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              <p className="text-white font-medium">{entry.player_name}</p>
                              <p className="text-gray-500 text-xs">{entry.masked_name}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-300">{entry.city}</td>
                          <td className="py-3 px-4 text-gray-300">{entry.game_name}</td>
                          <td className="py-3 px-4 text-center">
                            <span className="inline-block bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full font-bold">
                              {entry.score}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="inline-block bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full font-semibold">
                              {entry.points}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center text-gray-400 text-sm">
                            {new Date(entry.timestamp).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-12 text-gray-500">
                          <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No leaderboard data available</p>
                          <p className="text-sm">Try adjusting your filters</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {leaderboard.length > 0 && (
                <div className="mt-4 text-center text-gray-400">
                  Showing {leaderboard.length} entries
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact Messages Tab */}
        {activeTab === 'contact' && (
          <div className="space-y-6">
            <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-800">
              <h2 className="text-2xl font-bold text-yellow-400 mb-6 flex items-center gap-2">
                <MessageSquare className="w-6 h-6" />
                Contact Form Submissions
              </h2>

              {/* Filter */}
              <div className="mb-6 flex gap-4">
                <select
                  value={contactFilter}
                  onChange={(e) => {
                    setContactFilter(e.target.value);
                  }}
                  className="bg-black/30 text-white border border-gray-700 rounded-lg px-4 py-2"
                >
                  <option value="all">All Messages</option>
                  <option value="pending">Pending</option>
                  <option value="responded">Responded</option>
                  <option value="closed">Closed</option>
                </select>
                <button
                  onClick={fetchContactMessages}
                  className="bg-yellow-400 hover:bg-yellow-500 text-black font-semibold px-4 py-2 rounded-lg transition-colors"
                >
                  Refresh
                </button>
              </div>

              {/* Messages List */}
              <div className="space-y-4">
                {contactMessages.length > 0 ? (
                  contactMessages.map((message, idx) => (
                    <div
                      key={idx}
                      className="bg-black/30 border border-gray-700 rounded-lg p-6 hover:bg-black/50 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-white font-bold text-lg">{message.name}</h3>
                          <div className="flex items-center gap-4 text-gray-400 text-sm mt-1">
                            <span className="flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              {message.email}
                            </span>
                            {message.phone && (
                              <span>📞 {message.phone}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              message.status === 'pending'
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : message.status === 'responded'
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-gray-500/20 text-gray-400'
                            }`}
                          >
                            {message.status.toUpperCase()}
                          </span>
                          <span className="text-gray-500 text-xs">
                            {new Date(message.submitted_at).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>

                      <div className="mb-3">
                        <span className="text-blue-400 text-sm font-semibold">
                          Topic: {message.topic}
                        </span>
                      </div>

                      <div className="bg-gray-800/50 rounded p-4 mb-4">
                        <p className="text-gray-300 whitespace-pre-wrap">{message.message}</p>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        {message.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleUpdateContactStatus(message.id, 'responded')}
                              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                            >
                              Mark as Responded
                            </button>
                            <button
                              onClick={() => handleUpdateContactStatus(message.id, 'closed')}
                              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                            >
                              Close
                            </button>
                          </>
                        )}
                        {message.status === 'responded' && (
                          <button
                            onClick={() => handleUpdateContactStatus(message.id, 'closed')}
                            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                          >
                            Close
                          </button>
                        )}
                        {message.status === 'closed' && (
                          <button
                            onClick={() => handleUpdateContactStatus(message.id, 'pending')}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                          >
                            Reopen
                          </button>
                        )}
                        <a
                          href={`mailto:${message.email}?subject=Re: ${message.topic}`}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors inline-flex items-center gap-2"
                        >
                          <Mail className="w-4 h-4" />
                          Reply via Email
                        </a>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No contact messages yet</p>
                  </div>
                )}
              </div>

              {contactMessages.length > 0 && (
                <div className="mt-6 text-center text-gray-400">
                  Showing {contactMessages.length} message(s)
                </div>
              )}
            </div>
          </div>
        )}

        {/* PWA Installs Tab */}
        {activeTab === 'pwa' && (
          <div className="space-y-6">
            <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-800">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-yellow-400 flex items-center gap-2">
                  <Smartphone className="w-6 h-6" />
                  PWA Install Analytics
                </h2>
                <div className="flex items-center gap-4">
                  <select
                    value={pwaDays}
                    onChange={(e) => setPwaDays(Number(e.target.value))}
                    className="bg-black/30 text-white border border-gray-700 rounded-lg px-4 py-2"
                  >
                    <option value={7}>Last 7 Days</option>
                    <option value={30}>Last 30 Days</option>
                    <option value={90}>Last 90 Days</option>
                  </select>
                  <button
                    onClick={fetchPWAStats}
                    disabled={pwaLoading}
                    className="bg-yellow-400 hover:bg-yellow-500 text-black font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {pwaLoading ? 'Loading...' : 'Refresh'}
                  </button>
                </div>
              </div>

              {pwaLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400 mx-auto mb-4"></div>
                  <p className="text-gray-400">Loading PWA statistics...</p>
                </div>
              ) : pwaStats ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 rounded-lg p-6 border border-blue-500/30">
                      <div className="flex items-center gap-3 mb-2">
                        <Smartphone className="w-8 h-8 text-blue-400" />
                        <h3 className="text-sm text-gray-400">Total Installs</h3>
                      </div>
                      <p className="text-3xl font-bold text-white">{pwaStats.total_installs}</p>
                      <p className="text-xs text-gray-500 mt-1">in last {pwaStats.date_range.days} days</p>
                    </div>

                    <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 rounded-lg p-6 border border-green-500/30">
                      <div className="flex items-center gap-3 mb-2">
                        <Users className="w-8 h-8 text-green-400" />
                        <h3 className="text-sm text-gray-400">Mobile Installs</h3>
                      </div>
                      <p className="text-3xl font-bold text-white">
                        {pwaStats.device_breakdown.mobile || 0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {pwaStats.total_installs > 0 
                          ? Math.round((pwaStats.device_breakdown.mobile || 0) / pwaStats.total_installs * 100) 
                          : 0}% of total
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 rounded-lg p-6 border border-purple-500/30">
                      <div className="flex items-center gap-3 mb-2">
                        <BarChart3 className="w-8 h-8 text-purple-400" />
                        <h3 className="text-sm text-gray-400">Desktop Installs</h3>
                      </div>
                      <p className="text-3xl font-bold text-white">
                        {pwaStats.device_breakdown.desktop || 0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {pwaStats.total_installs > 0 
                          ? Math.round((pwaStats.device_breakdown.desktop || 0) / pwaStats.total_installs * 100) 
                          : 0}% of total
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-yellow-900/30 to-yellow-800/20 rounded-lg p-6 border border-yellow-500/30">
                      <div className="flex items-center gap-3 mb-2">
                        <Trophy className="w-8 h-8 text-yellow-400" />
                        <h3 className="text-sm text-gray-400">Avg Daily</h3>
                      </div>
                      <p className="text-3xl font-bold text-white">
                        {pwaStats.total_installs > 0 
                          ? Math.round(pwaStats.total_installs / pwaStats.date_range.days) 
                          : 0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">installs per day</p>
                    </div>
                  </div>

                  {/* Device & OS Breakdown */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {/* Device Types */}
                    <div className="bg-black/30 border border-gray-700 rounded-lg p-6">
                      <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                        <Smartphone className="w-5 h-5 text-blue-400" />
                        Device Types
                      </h3>
                      <div className="space-y-3">
                        {Object.entries(pwaStats.device_breakdown).map(([device, count]) => (
                          <div key={device} className="flex items-center justify-between">
                            <span className="text-gray-400 capitalize">{device}</span>
                            <div className="flex items-center gap-2">
                              <div className="bg-blue-600/30 rounded-full h-2 w-24 overflow-hidden">
                                <div 
                                  className="bg-blue-500 h-full"
                                  style={{ width: `${(count / pwaStats.total_installs * 100)}%` }}
                                />
                              </div>
                              <span className="text-white font-semibold">{count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Operating Systems */}
                    <div className="bg-black/30 border border-gray-700 rounded-lg p-6">
                      <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-green-400" />
                        Operating Systems
                      </h3>
                      <div className="space-y-3">
                        {Object.entries(pwaStats.os_breakdown).map(([os, count]) => (
                          <div key={os} className="flex items-center justify-between">
                            <span className="text-gray-400">{os}</span>
                            <div className="flex items-center gap-2">
                              <div className="bg-green-600/30 rounded-full h-2 w-24 overflow-hidden">
                                <div 
                                  className="bg-green-500 h-full"
                                  style={{ width: `${(count / pwaStats.total_installs * 100)}%` }}
                                />
                              </div>
                              <span className="text-white font-semibold">{count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Browsers */}
                    <div className="bg-black/30 border border-gray-700 rounded-lg p-6">
                      <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                        <Users className="w-5 h-5 text-purple-400" />
                        Browsers
                      </h3>
                      <div className="space-y-3">
                        {Object.entries(pwaStats.browser_breakdown).map(([browser, count]) => (
                          <div key={browser} className="flex items-center justify-between">
                            <span className="text-gray-400">{browser}</span>
                            <div className="flex items-center gap-2">
                              <div className="bg-purple-600/30 rounded-full h-2 w-24 overflow-hidden">
                                <div 
                                  className="bg-purple-500 h-full"
                                  style={{ width: `${(count / pwaStats.total_installs * 100)}%` }}
                                />
                              </div>
                              <span className="text-white font-semibold">{count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Daily Installs Chart */}
                  {pwaStats.daily_installs && pwaStats.daily_installs.length > 0 && (
                    <div className="bg-black/30 border border-gray-700 rounded-lg p-6">
                      <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-yellow-400" />
                        Daily Install Trend
                      </h3>
                      <div className="space-y-2">
                        {pwaStats.daily_installs.slice(-14).map((day) => (
                          <div key={day.date} className="flex items-center gap-3">
                            <span className="text-gray-400 text-sm w-24">
                              {new Date(day.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
                            </span>
                            <div className="flex-1 bg-gray-800 rounded-full h-6 overflow-hidden">
                              <div 
                                className="bg-yellow-500 h-full flex items-center justify-end px-2"
                                style={{ width: `${Math.max(day.count / Math.max(...pwaStats.daily_installs.map(d => d.count)) * 100, 5)}%` }}
                              >
                                <span className="text-white text-xs font-semibold">{day.count}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent Installs */}
                  {pwaStats.recent_installs && pwaStats.recent_installs.length > 0 && (
                    <div className="bg-black/30 border border-gray-700 rounded-lg p-6 mt-6">
                      <h3 className="text-white font-bold mb-4">Recent Installs</h3>
                      <div className="space-y-2">
                        {pwaStats.recent_installs.slice().reverse().map((install, idx) => (
                          <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0">
                            <div className="flex items-center gap-4">
                              <Smartphone className="w-4 h-4 text-blue-400" />
                              <span className="text-gray-400 text-sm">{install.device_type || 'Unknown'}</span>
                              <span className="text-gray-500 text-sm">{install.os || 'Unknown'}</span>
                              <span className="text-gray-500 text-sm">{install.browser || 'Unknown'}</span>
                            </div>
                            <span className="text-gray-500 text-xs">
                              {formatDateIST(install.installed_at)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Smartphone className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No PWA install data available</p>
                  <p className="text-sm mt-2">Click &quot;Refresh&quot; to load statistics</p>
                </div>
              )}
            </div>
          </div>
        )}


        {/* Winners Tab */}
        {activeTab === 'winners' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Trophy className="w-8 h-8 text-yellow-400" />
                Winner Management
              </h2>
              <button
                onClick={fetchWinners}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>

            {/* Pending Winners - Need Verification */}
            {pendingWinners.length > 0 && (
              <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Award className="w-6 h-6 text-red-400" />
                  ⚠️ Pending Verification ({pendingWinners.length})
                </h3>
                <div className="space-y-4">
                  {pendingWinners.map(winner => (
                    <div key={winner.winner_id} className="bg-black/30 border border-gray-700 rounded-lg p-6">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left Side - Winner Info */}
                        <div>
                          <div className="flex items-center gap-3 mb-4">
                            <Trophy className="w-8 h-8 text-yellow-400" />
                            <div>
                              <h4 className="text-xl font-bold text-white">{winner.player_name}</h4>
                              <p className="text-gray-400 text-sm">{winner.game_name} Winner</p>
                            </div>
                          </div>

                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-blue-400" />
                              <span className="text-gray-400">Phone:</span>
                              <span className="text-white font-mono">{winner.player_phone}</span>
                              <button
                                onClick={() => window.open(`tel:${winner.player_phone}`)}
                                className="text-blue-400 hover:text-blue-300 ml-2"
                              >
                                Call
                              </button>
                            </div>

                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-blue-400" />
                              <span className="text-gray-400">Email:</span>
                              <span className="text-white">{winner.player_email}</span>
                            </div>

                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-blue-400" />
                              <span className="text-gray-400">City:</span>
                              <span className="text-white">{winner.player_city}, {winner.player_state}</span>
                            </div>

                            <div className="flex items-center gap-2 mt-4 p-3 bg-green-900/30 rounded-lg border border-green-700">
                              <Award className="w-5 h-5 text-green-400" />
                              <div>
                                <div className="text-green-400 font-semibold">Score: {winner.score}</div>
                                <div className="text-gray-400 text-xs">Prize: {winner.prize_name}</div>
                              </div>
                            </div>

                            <div className="text-gray-500 text-xs mt-2">
                              Competition: {new Date(winner.competition_date).toLocaleString('en-IN')}
                            </div>
                          </div>
                        </div>

                        {/* Right Side - Verification Form */}
                        <div className="bg-gray-800/50 rounded-lg p-4">
                          <h5 className="text-white font-semibold mb-3">Winner Verification</h5>
                          
                          <div className="space-y-3 mb-4 text-sm">
                            <label className="flex items-center gap-2 text-gray-300">
                              <input type="checkbox" className="rounded" />
                              <span>Called and verified identity</span>
                            </label>
                            <label className="flex items-center gap-2 text-gray-300">
                              <input type="checkbox" className="rounded" />
                              <span>Confirmed email and city</span>
                            </label>
                            <label className="flex items-center gap-2 text-gray-300">
                              <input type="checkbox" className="rounded" />
                              <span>Obtained delivery address</span>
                            </label>
                            <label className="flex items-center gap-2 text-gray-300">
                              <input type="checkbox" className="rounded" />
                              <span>Received winner photo</span>
                            </label>
                          </div>

                          <div className="space-y-3">
                            <div>
                              <label className="block text-gray-400 text-xs mb-1">Winner Photo URL</label>
                              <input
                                type="text"
                                placeholder="https://..."
                                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                                id={`photo-${winner.winner_id}`}
                              />
                            </div>

                            <div>
                              <label className="block text-gray-400 text-xs mb-1">Delivery Address</label>
                              <textarea
                                rows="3"
                                placeholder="Street, City, State, PIN"
                                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                                id={`address-${winner.winner_id}`}
                              ></textarea>
                            </div>

                            <div>
                              <label className="block text-gray-400 text-xs mb-1">Alternate Phone (Optional)</label>
                              <input
                                type="text"
                                placeholder="+91 xxxxxxxxxx"
                                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                                id={`alt-phone-${winner.winner_id}`}
                              />
                            </div>

                            <div>
                              <label className="block text-gray-400 text-xs mb-1">Verification Notes</label>
                              <textarea
                                rows="2"
                                placeholder="Add notes from verification call..."
                                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                                id={`notes-${winner.winner_id}`}
                              ></textarea>
                            </div>
                          </div>

                          <div className="flex gap-2 mt-4">
                            <button
                              onClick={async () => {
                                const photo = document.getElementById(`photo-${winner.winner_id}`).value;
                                const address = document.getElementById(`address-${winner.winner_id}`).value;
                                const altPhone = document.getElementById(`alt-phone-${winner.winner_id}`).value;
                                const notes = document.getElementById(`notes-${winner.winner_id}`).value;

                                if (!address) {
                                  toast.error('Please add delivery address');
                                  return;
                                }

                                try {
                                  await axios.post(`${BACKEND_URL}/api/admin/winner/verify`, {
                                    winner_id: winner.winner_id,
                                    verification_status: 'verified',
                                    verification_notes: notes,
                                    delivery_address: { full_address: address },
                                    alternate_phone: altPhone
                                  });

                                  if (photo) {
                                    await axios.post(`${BACKEND_URL}/api/admin/winner/announce`, {
                                      winner_id: winner.winner_id,
                                      announcement_text: `Congratulations ${winner.player_name} from ${winner.player_city}!`,
                                      winner_photo_url: photo
                                    });
                                    toast.success('Winner verified and announced!');
                                  } else {
                                    toast.success('Winner verified!');
                                  }

                                  fetchWinners();
                                } catch (error) {
                                  toast.error('Failed to verify winner');
                                }
                              }}
                              className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors font-semibold"
                            >
                              ✓ Verify & Announce
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm('Mark this winner as rejected?')) {
                                  try {
                                    await axios.post(`${BACKEND_URL}/api/admin/winner/verify`, {
                                      winner_id: winner.winner_id,
                                      verification_status: 'rejected',
                                      verification_notes: 'Verification failed',
                                      delivery_address: {},
                                      alternate_phone: ''
                                    });
                                    toast.success('Winner marked as rejected');
                                    fetchWinners();
                                  } catch (error) {
                                    toast.error('Failed to reject winner');
                                  }
                                }
                              }}
                              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                            >
                              ✗ Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All Winners Table */}
            <div className="bg-black/30 border border-gray-700 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">All Winners ({allWinners.length})</h3>
              
              {allWinners.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Trophy className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No winners yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left text-gray-400 text-sm font-semibold py-3 px-4">Winner</th>
                        <th className="text-left text-gray-400 text-sm font-semibold py-3 px-4">Game</th>
                        <th className="text-left text-gray-400 text-sm font-semibold py-3 px-4">Score</th>
                        <th className="text-left text-gray-400 text-sm font-semibold py-3 px-4">Prize</th>
                        <th className="text-left text-gray-400 text-sm font-semibold py-3 px-4">Status</th>
                        <th className="text-left text-gray-400 text-sm font-semibold py-3 px-4">Delivery</th>
                        <th className="text-left text-gray-400 text-sm font-semibold py-3 px-4">Date</th>
                        <th className="text-left text-gray-400 text-sm font-semibold py-3 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allWinners.map(winner => (
                        <tr key={winner.winner_id} className="border-b border-gray-800 hover:bg-gray-800/30">
                          <td className="py-3 px-4">
                            <div className="text-white font-semibold">{winner.player_name}</div>
                            <div className="text-gray-400 text-xs">{winner.player_city}</div>
                          </td>
                          <td className="py-3 px-4 text-gray-300 text-sm">{winner.game_name}</td>
                          <td className="py-3 px-4 text-green-400 font-bold">{winner.score}</td>
                          <td className="py-3 px-4 text-gray-300 text-sm">{winner.prize_name}</td>
                          <td className="py-3 px-4">
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                              winner.verification_status === 'verified' ? 'bg-green-900/30 text-green-400 border border-green-700' :
                              winner.verification_status === 'rejected' ? 'bg-red-900/30 text-red-400 border border-red-700' :
                              'bg-yellow-900/30 text-yellow-400 border border-yellow-700'
                            }`}>
                              {winner.verification_status}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                              winner.delivery_status === 'delivered' ? 'bg-green-900/30 text-green-400 border border-green-700' :
                              winner.delivery_status === 'shipped' ? 'bg-blue-900/30 text-blue-400 border border-blue-700' :
                              'bg-gray-700 text-gray-400'
                            }`}>
                              {winner.delivery_status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-400 text-xs">
                            {new Date(winner.competition_date).toLocaleDateString('en-IN')}
                          </td>
                          <td className="py-3 px-4">
                            {winner.verification_status === 'verified' && winner.delivery_status === 'pending' && (
                              <button
                                onClick={async () => {
                                  const tracking = prompt('Enter tracking number:');
                                  if (tracking) {
                                    try {
                                      await axios.post(`${BACKEND_URL}/api/admin/winner/delivery`, {
                                        winner_id: winner.winner_id,
                                        delivery_status: 'shipped',
                                        tracking_number: tracking
                                      });
                                      toast.success('Marked as shipped');
                                      fetchWinners();
                                    } catch (error) {
                                      toast.error('Failed to update');
                                    }
                                  }
                                }}
                                className="text-blue-400 hover:text-blue-300 text-sm"
                              >
                                Mark Shipped
                              </button>
                            )}
                            {winner.delivery_status === 'shipped' && (
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.post(`${BACKEND_URL}/api/admin/winner/delivery`, {
                                      winner_id: winner.winner_id,
                                      delivery_status: 'delivered',
                                      tracking_number: winner.tracking_number
                                    });
                                    toast.success('Marked as delivered');
                                    fetchWinners();
                                  } catch (error) {
                                    toast.error('Failed to update');
                                  }
                                }}
                                className="text-green-400 hover:text-green-300 text-sm"
                              >
                                Mark Delivered
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminDashboard;
